import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
} from "@mui/material";
import dayjs from "dayjs";
import { MailingAddressResponse } from "../../../../../../../../api/services/client/models";
import { MuiBox, MuiTypography } from "../../../../../../../../components";
import { DATE_FORMAT } from "../../../../../../../../helpers/constants";
import {
  convertToUserFormat,
  getAmountWithCurrency,
  getValueOrBlank,
} from "../../../../../../../../helpers/utils";
import { useSiteCore } from "../../../../../../../../hooks/useSiteCore";
import { FeesAndExpenseRow } from "../../../../../../modelsAndSchemas/Models";
import { downloadProformaInvoiceSiteCoreKeys } from "../../../../../../siteCore/SiteCoreKeys";
import { ProformaPdfTemplateWrapper } from "./ProformaPdfTemplate.style";

interface ProformaPdfTemplateProps {
  mailingAddress: MailingAddressResponse;
  billingEmail: string;
  programName: string;
  clientName: string;
  expenseDetails: FeesAndExpenseRow[];
  participantName: string;
  totalInvoiceAmount: number;
  billingCurrency: string;
  paymentTerms?: string;
  header?: string;
  footer1?: string;
  footer2?: string;
  footer3?: string;
}

export const ProformaPdfTemplate = ({
  mailingAddress,
  billingEmail,
  programName,
  participantName,
  expenseDetails,
  clientName,
  totalInvoiceAmount,
  billingCurrency,
  paymentTerms,
  header,
  footer1,
  footer2,
  footer3,
}: ProformaPdfTemplateProps) => {
  const {
    useFetchLabel,
    // fetchMediaListImage
  } = useSiteCore();
  const getLabel = useFetchLabel(
    downloadProformaInvoiceSiteCoreKeys.downloadProformaInvoice,
  );
  // const siteCoreMediaImg = fetchMediaListImage(
  //   downloadProformaInvoiceSiteCoreKeys.downloadProformaInvoice,
  //   downloadProformaInvoiceSiteCoreKeys.PDFLogo,
  //   downloadProformaInvoiceSiteCoreKeys.mediumImg,
  // );

  const addressParts = [
    mailingAddress?.address1,
    mailingAddress?.address2,
    mailingAddress?.address3,
    mailingAddress?.countryName,
    mailingAddress?.city,
    mailingAddress?.stateId,
    mailingAddress?.postalCode,
  ];

  const nonEmptyParts = addressParts.filter((part) => part?.trim()); // remove empty/undefined
  return (
    <ProformaPdfTemplateWrapper>
      <MuiBox textAlign="right">
        <img
          className="right-management-logo"
          src="/images/footerTalentSolutionImg.png"
        />
        {/* <img
          className="right-management-logo"
          src={siteCoreMediaImg?.ImageUrl}
          alt={siteCoreMediaImg?.AltText}
          crossOrigin="anonymous"
        /> */}
      </MuiBox>
      <MuiBox className="sub-title">
        <MuiTypography variant="h3Bold">
          {getLabel(downloadProformaInvoiceSiteCoreKeys.PrivateAndConfidential)}
        </MuiTypography>
      </MuiBox>
      <MuiBox justifyContent="space-between" display="flex" mb={4} gap={5}>
        {/* Left: Address */}
        <MuiBox flex={1}>
          <MuiBox className="header-section">
            <MuiTypography>{clientName}</MuiTypography>
            {nonEmptyParts.map((part, index) => (
              <MuiTypography key={part}>
                {part}
                {index < nonEmptyParts.length - 1 ? "," : ""}
              </MuiTypography>
            ))}
          </MuiBox>

          <MuiBox className="email-to-section">
            <MuiTypography className="bold-label">
              {getLabel(downloadProformaInvoiceSiteCoreKeys.emailTo)}
            </MuiTypography>
            <MuiBox>{billingEmail}</MuiBox>
          </MuiBox>

          <MuiBox className="date-section">
            <MuiTypography className="bold-label">
              {getLabel(downloadProformaInvoiceSiteCoreKeys.date)}
            </MuiTypography>
            <MuiBox>
              {convertToUserFormat(dayjs().toString(), DATE_FORMAT.DD_MMM_YYYY)}
            </MuiBox>
          </MuiBox>
        </MuiBox>
        {/* Right: Header */}
        <MuiBox className="header-section right-header" flex={1}>
          <MuiTypography>{header}</MuiTypography>
        </MuiBox>
      </MuiBox>
      <MuiBox className="table-title">
        <MuiTypography variant="headerBold">
          {getLabel(downloadProformaInvoiceSiteCoreKeys.Quote)}
        </MuiTypography>
      </MuiBox>
      <MuiBox className="program-details-section">
        <MuiTypography className="bold-label">
          {getLabel(downloadProformaInvoiceSiteCoreKeys.participant)}:
        </MuiTypography>
        <MuiBox>{participantName}</MuiBox>
      </MuiBox>
      <MuiBox className="program-details-section">
        <MuiTypography className="bold-label">
          {getLabel(downloadProformaInvoiceSiteCoreKeys.program)}:
        </MuiTypography>
        <MuiBox>
          <MuiTypography>{programName}</MuiTypography>
        </MuiBox>
      </MuiBox>
      <Table aria-label="Download proforma invoice pdf table">
        <TableHead>
          <TableRow>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getLabel(
                  downloadProformaInvoiceSiteCoreKeys.feeOrExpenseCategoryColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getLabel(
                  downloadProformaInvoiceSiteCoreKeys.expenseCategoryColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="left">
              <MuiTypography variant="h3Bold">
                {getLabel(
                  downloadProformaInvoiceSiteCoreKeys.feeOrExpenseTypeColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell align="right">
              <MuiTypography variant="h3Bold">
                {getLabel(downloadProformaInvoiceSiteCoreKeys.taxColumn)}
              </MuiTypography>
            </TableCell>
            <TableCell align="right">
              <MuiTypography variant="h3Bold">
                {getLabel(
                  downloadProformaInvoiceSiteCoreKeys.finalLineItemValueColumn,
                )}
              </MuiTypography>
            </TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {expenseDetails.map((expense) => (
            <TableRow key={expense.id}>
              <TableCell>
                <MuiTypography variant="h3">{expense?.typeName}</MuiTypography>
              </TableCell>
              <TableCell>
                <MuiTypography variant="h3">
                  {expense.expenseCategoryName}
                </MuiTypography>
              </TableCell>
              <TableCell>
                <MuiTypography variant="h3">
                  {expense.feeOrExpenseType}
                </MuiTypography>
              </TableCell>
              <TableCell align="right" className="vertical-align-bottom">
                {expense?.taxDetails?.map((taxDetail) => (
                  <MuiTypography variant="h3" key={taxDetail.taxType?.id}>
                    {taxDetail.taxType?.name}
                  </MuiTypography>
                ))}
              </TableCell>
              <TableCell align="right" className="vertical-align-bottom">
                <MuiTypography variant="h3">
                  {getAmountWithCurrency(
                    expense.billingAmount,
                    billingCurrency,
                  )}
                </MuiTypography>
                {expense?.taxDetails?.map((taxDetail) => (
                  <MuiTypography variant="h3" key={taxDetail.amount}>
                    {getAmountWithCurrency(
                      taxDetail.amount ?? 0,
                      billingCurrency,
                    )}
                  </MuiTypography>
                ))}
              </TableCell>
            </TableRow>
          ))}
          <TableRow>
            <TableCell colSpan={3}>
              <MuiTypography variant="h3Bold">
                {getLabel(
                  downloadProformaInvoiceSiteCoreKeys.totalAmountColumn,
                )}
              </MuiTypography>
            </TableCell>
            <TableCell colSpan={2} align="right">
              <MuiTypography variant="h3Bold">
                {getAmountWithCurrency(totalInvoiceAmount, billingCurrency)}
              </MuiTypography>
            </TableCell>
          </TableRow>
        </TableBody>
      </Table>

      <MuiBox className="payment-notice-section">
        <MuiTypography fontWeight="bold">
          {getLabel(downloadProformaInvoiceSiteCoreKeys.paymentTerms)}
          {": "}
          {getValueOrBlank(paymentTerms)}
        </MuiTypography>
      </MuiBox>

      <MuiBox className="footer-section">
        <MuiTypography>{footer1}</MuiTypography>
        <MuiTypography>{footer2}</MuiTypography>
        <MuiTypography>{footer3}</MuiTypography>
      </MuiBox>
    </ProformaPdfTemplateWrapper>
  );
};
